import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import io from 'socket.io-client';
import API_CONFIG from './config/api';
import { formatarDataInteligente } from './utils/formatarData';
import DetalhesSimulacao from './DetalhesSimulacao';

const VisualizacaoAnalista = ({ onLogout }) => {
  const [simulacoes, setSimulacoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState(null);
  const [simulacaoSelecionada, setSimulacaoSelecionada] = useState(null);
  const [notificacoes, setNotificacoes] = useState([]);
  const [contadorNotificacoes, setContadorNotificacoes] = useState(0);
  const [showNotificacoes, setShowNotificacoes] = useState(false);
  const [filtroStatus, setFiltroStatus] = useState('Pendente');
  const [searchTerm, setSearchTerm] = useState('');
  const [simulacaoAguardandoConfirmacao, setSimulacaoAguardandoConfirmacao] = useState(null);
  const [showConfigurações, setShowConfiguracoes] = useState(false);
  const [bancosOrdenados, setBancosOrdenados] = useState([]);
  const [draggedBanco, setDraggedBanco] = useState(null);
  const [bancosModificados, setBancosModificados] = useState(false);
  const [salvandoConfiguracoes, setSalvandoConfiguracoes] = useState(false);
  const socketRef = useRef(null);

  // Função para ordenar simulações: mais recentes primeiro dentro de cada status, prioridades no topo
  const ordenarSimulacoes = (lista) => {
    return [...lista].sort((a, b) => {
      // Prioridades primeiro
      if (a.clienteNaLoja && !b.clienteNaLoja) return -1;
      if (!a.clienteNaLoja && b.clienteNaLoja) return 1;
      
      // Para Pendentes, ordenar por mais antigo (primeiro a chegar)
      if (a.status === 'Pendente' && b.status === 'Pendente') {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      
      // Dentro do mesmo grupo para outros status, mais recentes primeiro
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  // Filtrar simulações por status e busca
  const simulacoesFiltradas = simulacoes.filter(s => {
    const batidastatus = s.status === filtroStatus;
    if (!batidastatus) return false;

    if (!searchTerm) return true;

    const termo = searchTerm.toLowerCase();
    return (
      s.nome?.toLowerCase().includes(termo) ||
      s.usuario_id?.nome?.toLowerCase().includes(termo) ||
      s.veiculo?.toLowerCase().includes(termo) ||
      s.placa?.toLowerCase().includes(termo)
    );
  });

  // Aplicar limite de 20 itens para "Em análise" e "Finalizada" quando não houver busca
  const simulacoesExibidas = (searchTerm || filtroStatus === 'Pendente') 
    ? simulacoesFiltradas 
    : simulacoesFiltradas.slice(0, 20);

  // Função para agrupar simulações por CPF (usado em Finalizadas)
  const agruparPorCliente = (simulacoes) => {
    const grupos = {};
    simulacoes.forEach(sim => {
      const chave = sim.cpf || 'Sem CPF';
      if (!grupos[chave]) {
        grupos[chave] = {
            cpf: chave,
            simulacoes: [],
            nomes: new Set()
        };
      }
      grupos[chave].simulacoes.push(sim);
      if (sim.nome) grupos[chave].nomes.add(sim.nome);
    });

    return Object.values(grupos).map(grupo => {
        // Ordenar simulações pela mais recente
        const simsOrdenadas = grupo.simulacoes.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        // Último nome é o da simulação mais recente
        const ultimoNome = simsOrdenadas[0]?.nome || 'Sem Nome';
        
        return {
            id: grupo.cpf, // Usar CPF como identificador único
            ultimoNome,
            todosNomes: Array.from(grupo.nomes),
            simulacoes: simsOrdenadas
        };
    });
  };

  const simulacoesAgrupadas = filtroStatus === 'Finalizada' ? agruparPorCliente(simulacoesExibidas) : null;
  const [pastasExpandidas, setPastasExpandidas] = useState({});

  const togglePasta = (id) => {
    setPastasExpandidas(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  useEffect(() => {
    verificarAutenticacao();
    carregarSimulacoes();
    carregarContadorNotificacoes();
    
    // Conectar ao WebSocket
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    const parsedUser = userData ? JSON.parse(userData) : null;
    
    socketRef.current = io(API_CONFIG.BASE_URL.replace('/api', ''), {
      transports: ['websocket', 'polling'],
      path: '/socket.io',
      reconnectionAttempts: 5,
      reconnectionDelay: 1000
    });

    socketRef.current.on('connect', () => {
      console.log('✅ Socket conectado com ID:', socketRef.current.id);
      
      // Autenticar usuário no socket
      if (parsedUser?._id) {
        socketRef.current.emit('authenticate', parsedUser._id);
      }

      // Entrar na sala de simulações da loja (mais seguro e eficiente)
      const lojaId = parsedUser?.loja?.id || parsedUser?.loja_id;
      if (lojaId) {
        socketRef.current.emit('join-simulacoes', lojaId);
      } else {
        socketRef.current.emit('join-simulacoes');
      }
    });

    socketRef.current.on('connect_error', (error) => {
      console.error('❌ Erro de conexão socket:', error);
    });

    socketRef.current.on('disconnect', (reason) => {
      console.log('⚠️ Socket desconectado:', reason);
    });

    // Event listeners para simulações
    socketRef.current.on('nova-simulacao', (novaSimulacao) => {
      console.log('🔔 Nova simulação recebida via socket:', novaSimulacao._id);
      setSimulacoes(prev => {
        const existe = prev.some(s => s._id === novaSimulacao._id);
        if (existe) return prev;
        
        // Se temos lojaId, ignorar simulações de outras lojas (segurança extra frontend)
        const lojaIdUser = parsedUser?.loja?.id || parsedUser?.loja_id;
        if (lojaIdUser && novaSimulacao.loja_id && novaSimulacao.loja_id.toString() !== lojaIdUser.toString()) {
          return prev;
        }

        const novaLista = [...prev, novaSimulacao];
        return ordenarSimulacoes(novaLista);
      });
    });

    socketRef.current.on('atualizar-simulacao', (simulacaoAtualizada) => {
      console.log('🔄 Simulação atualizada via socket:', simulacaoAtualizada._id);
      setSimulacoes(prev => {
        // Se temos lojaId, ignorar simulações de outras lojas
        const lojaIdUser = parsedUser?.loja?.id || parsedUser?.loja_id;
        if (lojaIdUser && simulacaoAtualizada.loja_id && simulacaoAtualizada.loja_id.toString() !== lojaIdUser.toString()) {
          return prev;
        }

        const listaAtualizada = prev.map(s => s._id === simulacaoAtualizada._id ? simulacaoAtualizada : s);
        return ordenarSimulacoes(listaAtualizada);
      });
    });

    socketRef.current.on('excluir-simulacao', (data) => {
      setSimulacoes(prev => prev.filter(s => s._id !== data.id));
    });

    // Event listener para notificações
    socketRef.current.on('nova-notificacao', (notificacao) => {
      setNotificacoes(prev => [notificacao, ...prev]);
      setContadorNotificacoes(prev => prev + 1);
      
      // Reproduzir som de notificação (opcional)
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZRA0PVant6qxbGAxJouHzvG0gBSyB0PPchzEHImXE7eCVPA==');
        audio.volume = 0.3;
        audio.play().catch(() => {}); // Ignorar erros de autoplay
      } catch (error) {
      }
    });

    // Cleanup
    return () => {
      if (socketRef.current) {
        if (parsedUser?.loja_id) {
          socketRef.current.emit('leave-simulacoes', parsedUser.loja_id);
        } else {
          socketRef.current.emit('leave-simulacoes');
        }
        socketRef.current.disconnect();
      }
    };
  }, []);

  // Carregar bancos quando o modal de configurações abre
  useEffect(() => {
    if (showConfigurações && bancosOrdenados.length === 0) {
      const carregarBancos = async () => {
        try {
          const token = localStorage.getItem('token');
          const userData = localStorage.getItem('user');
          const parsedUser = userData ? JSON.parse(userData) : null;

          // Primeiro, tentar carregar a ordem de bancos salva no usuário
          let bancosParaUsar = null;
          if (parsedUser?.configuracoes_analista?.ordem_bancos && 
              Array.isArray(parsedUser.configuracoes_analista.ordem_bancos) &&
              parsedUser.configuracoes_analista.ordem_bancos.length > 0) {
            bancosParaUsar = parsedUser.configuracoes_analista.ordem_bancos;
          } else {
            // Se não tiver configuração salva, buscar bancos da API
            const response = await axios.get(`${API_CONFIG.API_URL}/bancos?ativos=true`, {
              headers: { 'x-auth-token': token }
            });

            if (response.data.success && response.data.data) {
              bancosParaUsar = response.data.data;
            }
          }

          if (bancosParaUsar) {
            setBancosOrdenados(bancosParaUsar);
          }
        } catch (error) {
          console.error('Erro ao carregar bancos:', error);
          // Usar bancos padrão em caso de erro
          setBancosOrdenados([
            { nome: 'Santander', _id: '1' },
            { nome: 'Itaú', _id: '2' },
            { nome: 'BV', _id: '3' },
            { nome: 'PAN', _id: '4' },
            { nome: 'C6', _id: '5' }
          ]);
        }
      };

      carregarBancos();
    }
  }, [showConfigurações, bancosOrdenados.length]);

  const verificarAutenticacao = () => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');
    
    if (!token || !userData) {
      window.location.href = '/';
      return;
    }

    const parsedUser = JSON.parse(userData);
    
    // Verificar se � Analista ou Gerente
    if (parsedUser.cargo !== 'Analista' && parsedUser.cargo !== 'Gerente') {
      window.location.href = '/';
      return;
    }

    setUser(parsedUser);
  };

  const carregarSimulacoes = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/simulacoes`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        // Ordenar: prioridade primeiro (mais antigas no topo), depois não-prioridade (mais antigas no topo)
        const simulacoesOrdenadas = ordenarSimulacoes(response.data.data);
        setSimulacoes(simulacoesOrdenadas);
      }
    } catch (error) {
      if (error.response && error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    } finally {
      setLoading(false);
    }
  };

  const handleIniciarAnalise = async (simulacao) => {
    // Resetar confirmação de exclusão ao clicar em uma simulação
    setSimulacaoAguardandoConfirmacao(null);
    
    try {
      const token = localStorage.getItem('token');
      
      // Atualizar status para "Em análise"
      await axios.put(
        `${API_CONFIG.API_URL}/simulacoes/${simulacao._id}`,
        { status: 'Em análise' },
        { headers: { 'x-auth-token': token } }
      );

      // Navegar para detalhes da simulação
      setSimulacaoSelecionada(simulacao._id);
      
    } catch (error) {
    }
  };

  const handleExcluirSimulacao = async (simulacaoId) => {
    // Se já está aguardando confirmação, executar exclusão
    if (simulacaoAguardandoConfirmacao === simulacaoId) {
      try {
        const token = localStorage.getItem('token');
        await axios.delete(`${API_CONFIG.API_URL}/simulacoes/${simulacaoId}`, {
          headers: { 'x-auth-token': token }
        });
        
        // Remover imediatamente da lista para feedback visual imediato
        setSimulacoes(prev => prev.filter(s => s._id !== simulacaoId));
        setSimulacaoAguardandoConfirmacao(null);
      } catch (error) {
        console.error('Erro ao excluir simulação:', error);
        setSimulacaoAguardandoConfirmacao(null);
      }
    } else {
      // Primeiro clique - mostrar em evidência
      setSimulacaoAguardandoConfirmacao(simulacaoId);
    }
  };

  const handleVoltarLista = () => {
    setSimulacaoSelecionada(null);
    carregarSimulacoes(); // Recarregar lista atualizada
  };

  const carregarNotificacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/notificacoes?limit=20`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        setNotificacoes(response.data.data);
        setContadorNotificacoes(response.data.naoLidas);
      }
    } catch (error) {
    }
  };

  const carregarContadorNotificacoes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.API_URL}/notificacoes/contador`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        setContadorNotificacoes(response.data.naoLidas);
      }
    } catch (error) {
    }
  };

  const marcarComoLida = async (notificacaoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_CONFIG.API_URL}/notificacoes/${notificacaoId}/marcar-lida`, {}, {
        headers: { 'x-auth-token': token }
      });
      await carregarNotificacoes();
      await carregarContadorNotificacoes();
    } catch (error) {
    }
  };

  const marcarTodasLidas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_CONFIG.API_URL}/notificacoes/marcar-todas-lidas`, {}, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        await carregarNotificacoes();
        setContadorNotificacoes(0);
      }
    } catch (error) {
    }
  };

  const excluirNotificacao = async (notificacaoId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_CONFIG.API_URL}/notificacoes/${notificacaoId}`, {
        headers: { 'x-auth-token': token }
      });
      await carregarNotificacoes();
      await carregarContadorNotificacoes();
    } catch (error) {
    }
  };

  const salvarConfiguracoes = async () => {
    try {
      setSalvandoConfiguracoes(true);
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      const parsedUser = userData ? JSON.parse(userData) : null;

      if (!parsedUser?.id) {
        console.error('Erro: Usuário não identificado');
        return;
      }

      // Salvar a ordem dos bancos
      const response = await axios.put(
        `${API_CONFIG.API_URL}/usuarios/${parsedUser.id}/configuracoes`,
        {
          configuracoes_analista: {
            ordem_bancos: bancosOrdenados
          }
        },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data.success) {
        // Atualizar localStorage com as novas configurações
        const usuarioAtualizado = { ...parsedUser };
        if (!usuarioAtualizado.configuracoes_analista) {
          usuarioAtualizado.configuracoes_analista = {};
        }
        usuarioAtualizado.configuracoes_analista.ordem_bancos = bancosOrdenados;
        localStorage.setItem('user', JSON.stringify(usuarioAtualizado));

        setBancosModificados(false);
        // Fechar modal após sucesso
        setShowConfiguracoes(false);
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
    } finally {
      setSalvandoConfiguracoes(false);
    }
  };

  // Se não houver usuário, mostrar carregando
  if (!user) {
    return (
      <div className="visualizacao-loading">
        <p>Verificando autenticação...</p>
      </div>
    );
  }

  return (
    <>
    <style>
      {`
        .visualizacao-main::-webkit-scrollbar {
          display: none;
        }
        .notificacoes-scroll::-webkit-scrollbar {
          display: none;
        }
      `}
    </style>
    <div className="visualizacao-container" style={{ 
      width: '100%', 
      height: '100vh', 
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      background: '#f0f0f0',
      position: 'absolute',
      top: 0,
      left: 0,
      borderRadius: 0
    }}>
      {simulacaoSelecionada ? (
        <DetalhesSimulacao simulacaoId={simulacaoSelecionada} onVoltar={handleVoltarLista} />
      ) : (
        <>
          <header style={{ 
            background: '#2d3748',
            padding: '8px 16px',
            color: 'white',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            flexShrink: 0
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <h1 style={{ fontSize: '15px', fontWeight: 700, margin: 0 }}>
                {filtroStatus === 'Pendente' ? 'Simulações Pendentes' : 
                 filtroStatus === 'Em análise' ? 'Simulações em Análise' : 
                 'Simulações Finalizadas'} ({simulacoesFiltradas.length})
              </h1>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ position: 'relative' }}>
                  <button 
                    onClick={() => {
                      setShowNotificacoes(!showNotificacoes);
                      if (!showNotificacoes) {
                        carregarNotificacoes();
                      }
                    }}
                    style={{
                      background: 'rgba(255,255,255,0.2)',
                      border: 'none',
                      borderRadius: '50%',
                      padding: '6px',
                      color: 'white',
                      cursor: 'pointer',
                      fontSize: '16px',
                      transition: 'all 0.2s',
                      width: '32px',
                      height: '32px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative'
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                  >
                    🔔
                    {contadorNotificacoes > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '-3px',
                        right: '-3px',
                        background: '#e74c3c',
                        color: 'white',
                        borderRadius: '50%',
                        width: '16px',
                        height: '16px',
                        fontSize: '9px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        border: '2px solid #2d3748'
                      }}>
                        {contadorNotificacoes > 9 ? '9+' : contadorNotificacoes}
                      </span>
                    )}
                  </button>
                </div>
                <button
                  onClick={() => setShowConfiguracoes(!showConfigurações)}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    padding: '6px',
                    color: 'white',
                    cursor: 'pointer',
                    fontSize: '16px',
                    transition: 'all 0.2s',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
                  onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
                  title="Configurações"
                >
                  ⚙️
                </button>
              </div>
            </div>
            
            {/* Filtro de Status */}
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
          <button
            onClick={() => setFiltroStatus('Pendente')}
            style={{
              flex: 1,
              background: filtroStatus === 'Pendente' ? '#007bff' : '#e8eaed',
              color: filtroStatus === 'Pendente' ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              fontWeight: filtroStatus === 'Pendente' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (filtroStatus !== 'Pendente') {
                e.currentTarget.style.background = '#dde1e6';
              }
            }}
            onMouseLeave={(e) => {
              if (filtroStatus !== 'Pendente') {
                e.currentTarget.style.background = '#e8eaed';
              }
            }}
          >
            Pendentes
          </button>
          <button
            onClick={() => setFiltroStatus('Em análise')}
            style={{
              flex: 1,
              background: filtroStatus === 'Em análise' ? '#007bff' : '#e8eaed',
              color: filtroStatus === 'Em análise' ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              fontWeight: filtroStatus === 'Em análise' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (filtroStatus !== 'Em análise') {
                e.currentTarget.style.background = '#dde1e6';
              }
            }}
            onMouseLeave={(e) => {
              if (filtroStatus !== 'Em análise') {
                e.currentTarget.style.background = '#e8eaed';
              }
            }}
          >
            Em análise
          </button>
          <button
            onClick={() => setFiltroStatus('Finalizada')}
            style={{
              flex: 1,
              background: filtroStatus === 'Finalizada' ? '#007bff' : '#e8eaed',
              color: filtroStatus === 'Finalizada' ? 'white' : '#333',
              border: 'none',
              borderRadius: '6px',
              padding: '10px 12px',
              fontSize: '13px',
              fontWeight: filtroStatus === 'Finalizada' ? 600 : 500,
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              if (filtroStatus !== 'Finalizada') {
                e.currentTarget.style.background = '#dde1e6';
              }
            }}
            onMouseLeave={(e) => {
              if (filtroStatus !== 'Finalizada') {
                e.currentTarget.style.background = '#e8eaed';
              }
            }}
          >
            Finalizadas
          </button>
        </div>

        {/* Barra de Pesquisa (visível apenas em Finalizada) */}
        {filtroStatus === 'Finalizada' && (
          <div style={{ marginTop: '12px' }}>
            <input
              type="text"
              placeholder="Buscar cliente, vendedor ou veículo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                background: 'white',
                color: '#333',
                fontSize: '13px',
                outline: 'none',
                transition: 'all 0.2s'
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = '#007bff';
                e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,123,255,0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = '#e0e0e0';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        )}
      </header>

      <main className="visualizacao-main" style={{ 
        flex: 1, 
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '16px',
        background: '#f9f9f9',
        scrollbarWidth: 'none', /* Firefox */
        msOverflowStyle: 'none'  /* IE and Edge */
      }}>
        {loading && simulacoes.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#666'
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⏳</div>
            <p style={{ fontSize: '13px' }}>Carregando simulações...</p>
          </div>
        ) : simulacoesFiltradas.length === 0 ? (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '12px' }}>📋</div>
            <p style={{ fontSize: '14px', fontWeight: 600 }}>Nenhuma simulação {filtroStatus === 'Pendente' ? 'pendente' : filtroStatus === 'Em análise' ? 'em análise' : 'finalizada'}</p>
            <small style={{ fontSize: '12px', color: '#bbb' }}>
              {filtroStatus === 'Pendente' ? 'Aguardando novas solicitações' : filtroStatus === 'Em análise' ? 'Nenhuma análise em andamento' : 'Nenhuma simulação finalizada'}
            </small>
          </div>
        ) : (
          <>
            {/* Renderizar agrupado por cliente em TABELA DE PASTAS se Finalizada, caso contrário renderizar normal em grid */}
            {filtroStatus === 'Finalizada' && simulacoesAgrupadas && simulacoesAgrupadas.length > 0 ? (
              <div style={{ width: '100%', overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  backgroundColor: 'white',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
                }}>
                  <thead>
                    <tr style={{ background: '#f7f9fc', borderBottom: '2px solid #e0e0e0' }}>
                      <th style={{ width: '40px', padding: '10px', textAlign: 'center', fontSize: '13px', fontWeight: 600, color: '#555' }}></th>
                      <th style={{ padding: '10px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: '#555' }}>Cliente</th>
                    </tr>
                  </thead>
                  <tbody>
                    {simulacoesAgrupadas.map((grupo) => (
                      <React.Fragment key={grupo.id}>
                        {/* LINHA DA PASTA */}
                        <tr 
                          onClick={() => togglePasta(grupo.id)}
                          style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid #e0e0e0',
                            background: pastasExpandidas[grupo.id] ? '#f0f7ff' : 'white',
                            transition: 'background 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = pastasExpandidas[grupo.id] ? '#f0f7ff' : '#f9f9f9'}
                          onMouseLeave={(e) => e.currentTarget.style.background = pastasExpandidas[grupo.id] ? '#f0f7ff' : 'white'}
                        >
                          <td style={{ textAlign: 'center', padding: '10px', fontSize: '16px' }}>
                            {pastasExpandidas[grupo.id] ? '📂' : '📁'}
                          </td>
                          <td style={{ padding: '10px', fontWeight: 500, color: '#2c3e50', fontSize: '13px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {grupo.ultimoNome}
                                {grupo.todosNomes && grupo.todosNomes.length > 1 && (
                                    <div 
                                        title={`Nomes utilizados:\n${grupo.todosNomes.map(n => '• ' + n).join('\n')}\nCPF: ${grupo.id}`}
                                        style={{ 
                                            cursor: 'help', 
                                            fontSize: '11px', 
                                            color: '#3182ce',
                                            marginLeft: '5px',
                                            fontWeight: 'bold'
                                        }}
                                    >
                                        ( i )
                                    </div>
                                )}
                            </div>
                          </td>
                        </tr>

                        {/* CONTEÚDO EXPANDIDO (SIMULAÇÕES) */}
                        {pastasExpandidas[grupo.id] && (
                          <tr style={{ backgroundColor: '#fdfdfd', borderBottom: '1px solid #e0e0e0' }}>
                            <td colSpan="2" style={{ padding: '0' }}>
                              <div style={{ padding: '8px 20px' }}>
                                {grupo.simulacoes.map((simulacao) => (
                                  <div
                                    key={simulacao._id}
                                    onClick={() => setSimulacaoSelecionada(simulacao._id)}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      padding: '10px 12px',
                                      borderBottom: '1px solid #f0f0f0',
                                      cursor: 'pointer',
                                      transition: 'all 0.2s',
                                      gap: '12px',
                                      background: 'white',
                                      borderRadius: '4px',
                                      marginBottom: '6px',
                                      fontSize: '12px'
                                    }}
                                    onMouseEnter={(e) => {
                                      e.currentTarget.style.background = '#f5f5f5';
                                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                                    }}
                                    onMouseLeave={(e) => {
                                      e.currentTarget.style.background = 'white';
                                      e.currentTarget.style.boxShadow = 'none';
                                    }}
                                  >
                                    {/* Veículo + Ano */}
                                    <div style={{ flex: '1 1 auto', minWidth: '150px' }}>
                                      <span style={{ color: '#333', fontWeight: 500 }}>
                                        {simulacao.veiculo || 'Veículo desconhecido'} {simulacao.ano ? `(${simulacao.ano})` : ''}
                                      </span>
                                    </div>

                                    {/* Data */}
                                    <div style={{ flex: '0 0 110px', color: '#888', textAlign: 'right', paddingRight: '8px' }}>
                                      {formatarDataInteligente(simulacao.createdAt)}
                                    </div>

                                    {/* Botão de excluir (apenas Gerente) */}
                                    {(user?.cargo === 'Gerente' || user?.cargo === 'gerente') && (
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleExcluirSimulacao(simulacao._id);
                                        }}
                                        style={{
                                          background: simulacaoAguardandoConfirmacao === simulacao._id ? '#e74c3c' : 'none',
                                          border: simulacaoAguardandoConfirmacao === simulacao._id ? '2px solid #c0392b' : 'none',
                                          cursor: 'pointer',
                                          fontSize: simulacaoAguardandoConfirmacao === simulacao._id ? '14px' : '12px',
                                          padding: simulacaoAguardandoConfirmacao === simulacao._id ? '4px 8px' : '0',
                                          color: simulacaoAguardandoConfirmacao === simulacao._id ? 'white' : '#e74c3c',
                                          lineHeight: 1,
                                          borderRadius: '4px',
                                          transition: 'all 0.2s ease',
                                          transform: simulacaoAguardandoConfirmacao === simulacao._id ? 'scale(1.05)' : 'scale(1)'
                                        }}
                                        title={simulacaoAguardandoConfirmacao === simulacao._id ? 'Clique novamente para confirmar exclusão' : 'Clique para excluir'}
                                      >
                                        🗑️
                                      </button>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Renderização normal para Pendente e Em análise */
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', 
                gap: '12px' 
              }}>
                {simulacoesExibidas.map((simulacao) => (
                  <div 
                    key={simulacao._id}
                    onClick={() => {
                      if (filtroStatus === 'Finalizada') {
                        setSimulacaoSelecionada(simulacao._id);
                      }
                    }}
                    style={{
                      background: simulacao.clienteNaLoja ? '#e8f5e9' : 'white',
                      borderRadius: '8px',
                      padding: filtroStatus === 'Finalizada' ? '12px' : '14px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                      border: simulacao.clienteNaLoja ? '2px solid #4caf50' : '1px solid #e8e8e8',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.background = simulacao.clienteNaLoja ? '#e8f5e9' : '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.background = simulacao.clienteNaLoja ? '#e8f5e9' : 'white';
                    }}
                  >
                    {/* Header do Card - Compacto */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      marginBottom: filtroStatus === 'Finalizada' ? '4px' : '8px'
                    }}>
                      {simulacao.clienteNaLoja && (
                        <span style={{
                          background: '#4caf50',
                          color: 'white',
                          fontSize: '9px',
                          fontWeight: 700,
                          padding: '2px 6px',
                          borderRadius: '10px',
                          textTransform: 'uppercase',
                          marginRight: 'auto'
                        }}>
                          🔔 Prioridade
                        </span>
                      )}
                      
                      <span style={{
                        fontSize: '10px',
                        color: '#888',
                        marginLeft: !simulacao.clienteNaLoja ? 'auto' : '0'
                      }}>
                        {formatarDataInteligente(simulacao.createdAt)}
                      </span>
                      
                      {/* Botão de excluir (apenas Gerente) */}
                      {(user?.cargo === 'Gerente' || user?.cargo === 'gerente') && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExcluirSimulacao(simulacao._id);
                          }}
                          style={{
                            background: simulacaoAguardandoConfirmacao === simulacao._id ? '#e74c3c' : 'none',
                            border: simulacaoAguardandoConfirmacao === simulacao._id ? '2px solid #c0392b' : 'none',
                            cursor: 'pointer',
                            fontSize: simulacaoAguardandoConfirmacao === simulacao._id ? '28px' : '14px',
                            padding: simulacaoAguardandoConfirmacao === simulacao._id ? '8px 12px' : '0 0 0 8px',
                            color: simulacaoAguardandoConfirmacao === simulacao._id ? 'white' : '#e74c3c',
                            lineHeight: 1,
                            borderRadius: '6px',
                            transition: 'all 0.2s ease',
                            transform: simulacaoAguardandoConfirmacao === simulacao._id ? 'scale(1.1)' : 'scale(1)'
                          }}
                          title={simulacaoAguardandoConfirmacao === simulacao._id ? 'Clique novamente para confirmar exclusão' : 'Clique para excluir'}
                        >
                          🗑️
                        </button>
                      )}
                    </div>

                    {/* Informações principais */}
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '4px',
                      marginBottom: filtroStatus === 'Finalizada' ? '0' : '8px'
                    }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '10px', color: '#999', fontWeight: 700, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Cliente
                        </span>
                        <span style={{ 
                          fontSize: '13px', 
                          fontWeight: 700, 
                          color: '#333'
                        }}>
                          {simulacao.nome || 'Não informado'}
                        </span>
                      </div>

                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ flex: 1 }}>
                          <span style={{ fontSize: '10px', color: '#999', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Vendedor</span>
                          <div style={{ 
                            fontSize: '12px', 
                            color: '#555',
                            marginTop: '2px'
                          }}>
                            {simulacao.usuario_id?.nome || 'Não informado'}
                          </div>
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: '10px', color: '#999', fontWeight: 700, marginBottom: '2px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Veículo
                        </span>
                        <span style={{ 
                          fontSize: '12px', 
                          color: '#c14427',
                          fontWeight: 600
                        }}>
                          {simulacao.veiculo || 'Não informado'} {simulacao.ano || ''}
                        </span>
                      </div>
                    </div>

                    {/* Botão de ação - Oculto em Finalizadas */}
                    {filtroStatus !== 'Finalizada' && (
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleIniciarAnalise(simulacao);
                        }}
                        style={{
                          width: '100%',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          padding: '10px',
                          fontSize: '13px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          marginTop: '4px'
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = '#0056b3')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = '#007bff')}
                      >
                        {filtroStatus === 'Pendente' ? '▶ Iniciar Análise' : '📝 Continuar'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* Mensagem de limite quando aplicável */}
        {!searchTerm && (filtroStatus === 'Em análise' || filtroStatus === 'Finalizada') && simulacoesFiltradas.length > 20 && (
          <div style={{ 
            padding: '12px', 
            textAlign: 'center', 
            fontSize: '11px', 
            color: '#888',
            background: 'rgba(0,0,0,0.03)',
            borderRadius: '8px',
            marginTop: '4px'
          }}>
            Exibindo as últimas 20 passagens. <br/>
            Use a busca acima para encontrar registros mais antigos.
          </div>
        )}
      </main>
        </>
      )}
    </div>

    {/* Tela fullscreen de notificações */}
    {showNotificacoes && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'white',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        animation: 'slideInRight 0.3s ease'
      }}>
        {/* Header */}
        <div style={{
          background: '#2d3748',
          padding: '16px',
          color: 'white',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Notificações</h2>
          <button
            onClick={() => setShowNotificacoes(false)}
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 12px',
              color: 'white',
              cursor: 'pointer',
              fontSize: '20px',
              fontWeight: 'bold',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.3)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
          >
            ×
          </button>
        </div>

        {/* Botão marcar todas como lidas */}
        {contadorNotificacoes > 0 && (
          <div style={{
            padding: '12px 16px',
            background: '#f8f9fa',
            borderBottom: '1px solid #e0e0e0'
          }}>
            <button
              onClick={marcarTodasLidas}
              style={{
                background: 'none',
                border: 'none',
                color: '#3498db',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 600,
                textDecoration: 'underline'
              }}
            >
              Marcar todas como lidas
            </button>
          </div>
        )}

        {/* Lista de notificações */}
        <div className="notificacoes-scroll" style={{
          flex: 1,
          overflowY: 'auto',
          background: '#f0f0f0',
          scrollbarWidth: 'none', /* Firefox */
          msOverflowStyle: 'none'  /* IE and Edge */
        }}>
          {notificacoes.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999'
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📭</div>
              <p style={{ fontSize: '14px', fontWeight: 600 }}>Nenhuma notificação</p>
            </div>
          ) : (
            notificacoes.map((notif) => (
              <div
                key={notif._id}
                style={{
                  padding: '16px',
                  background: notif.lida ? 'white' : '#e3f2fd',
                  borderBottom: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  transition: 'background 0.2s'
                }}
                onClick={async () => {
                  if (!notif.lida) {
                    await marcarComoLida(notif._id);
                  }
                  setShowNotificacoes(false);
                }}
                onTouchStart={(e) => {
                  e.currentTarget.style.background = notif.lida ? '#f8f9fa' : '#bbdefb';
                }}
                onTouchEnd={(e) => {
                  e.currentTarget.style.background = notif.lida ? 'white' : '#e3f2fd';
                }}
              >
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '8px'
                }}>
                  <strong style={{
                    fontSize: '14px',
                    color: '#333',
                    flex: 1,
                    paddingRight: '8px'
                  }}>
                    {notif.titulo}
                  </strong>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {!notif.lida && (
                      <span style={{
                        width: '8px',
                        height: '8px',
                        background: '#2196f3',
                        borderRadius: '50%'
                      }} />
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        excluirNotificacao(notif._id);
                      }}
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '20px',
                        color: '#e74c3c',
                        padding: '0',
                        lineHeight: '1'
                      }}
                    >
                      ×
                    </button>
                  </div>
                </div>
                <p style={{
                  margin: '0 0 8px 0',
                  fontSize: '13px',
                  color: '#666',
                  lineHeight: '1.4'
                }}
                dangerouslySetInnerHTML={{
                  __html: notif.mensagem.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                }}
                />
                <small style={{
                  color: '#999',
                  fontSize: '11px'
                }}>
                  {formatarDataInteligente(notif.createdAt)}
                </small>
              </div>
            ))
          )}
        </div>
      </div>
    )}

    {/* Modal de Configurações */}
    {showConfigurações && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{
          background: 'white',
          borderRadius: '0',
          boxShadow: 'none',
          width: '100%',
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {/* Simple Header with Close Button */}
          <div style={{
            background: 'white',
            padding: '16px 20px',
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#333' }}>⚙️ Configurações</h2>
            <button
              onClick={() => setShowConfiguracoes(false)}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: '#999',
                padding: '0',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              ×
            </button>
          </div>

          {/* Conteúdo */}
          <div style={{ flex: 1, overflow: 'auto', padding: '20px' }}>
            {/* Seção: Informações do Usuário */}
            <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#333' }}>
                  {user?.name || user?.nome || 'Usuário'} <span style={{ color: '#999', fontWeight: 400 }}>({user?.loja?.nome || 'Loja'})</span>
                </h3>
              </div>
              <button
                onClick={onLogout}
                style={{
                  padding: '6px 10px',
                  background: 'transparent',
                  border: '1px solid rgba(0,0,0,0.1)',
                  borderRadius: '4px',
                  color: '#666',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: 500,
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f5f5f5';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.borderColor = 'rgba(0,0,0,0.1)';
                }}
              >
                Sair
              </button>
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid #e0e0e0', margin: '20px 0' }} />

            <div style={{ marginBottom: '24px' }}>
              <h3 style={{ fontSize: '14px', fontWeight: 600, color: '#333', marginBottom: '12px', marginTop: 0 }}>
                🏦 Ordem dos Bancos
              </h3>
              <p style={{ fontSize: '12px', color: '#666', marginBottom: '12px' }}>
                Arraste os bancos para alterar a ordem de exibição
              </p>
              <div style={{
                background: '#f9f9f9',
                borderRadius: '8px',
                border: '1px solid #e0e0e0',
                overflow: 'hidden'
              }}>
                {bancosOrdenados && bancosOrdenados.length > 0 ? (
                  <div>
                    {bancosOrdenados.map((banco, index) => (
                      <div
                        key={banco._id}
                        draggable
                        onDragStart={() => setDraggedBanco(index)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => {
                          if (draggedBanco !== null && draggedBanco !== index) {
                            const novosBancos = [...bancosOrdenados];
                            const [draggedItem] = novosBancos.splice(draggedBanco, 1);
                            novosBancos.splice(index, 0, draggedItem);
                            setBancosOrdenados(novosBancos);
                            setBancosModificados(true);
                          }
                          setDraggedBanco(null);
                        }}
                        onDragEnd={() => setDraggedBanco(null)}
                        style={{
                          padding: '12px 16px',
                          borderBottom: index < bancosOrdenados.length - 1 ? '1px solid #e0e0e0' : 'none',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          cursor: 'grab',
                          background: draggedBanco === index ? '#e8f4f8' : 'transparent',
                          transition: 'all 0.2s',
                          opacity: draggedBanco === index ? 0.6 : 1
                        }}
                        onMouseEnter={(e) => {
                          if (draggedBanco !== index) {
                            e.currentTarget.style.background = '#f0f2f5';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (draggedBanco !== index) {
                            e.currentTarget.style.background = 'transparent';
                          }
                        }}
                      >
                        <span style={{ fontSize: '18px', cursor: 'grab' }}>⋮⋮</span>
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#333', flex: 1 }}>
                          {banco.nome}
                        </span>
                        <span style={{ fontSize: '11px', background: '#e0e0e0', padding: '4px 8px', borderRadius: '4px', color: '#666' }}>
                          #{index + 1}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    color: '#999',
                    fontSize: '13px'
                  }}>
                    Nenhum banco cadastrado
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            borderTop: '1px solid #e0e0e0',
            padding: '16px 20px',
            background: '#f9f9f9',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '8px'
          }}>
            <button
              onClick={salvarConfiguracoes}
              disabled={!bancosModificados || salvandoConfiguracoes}
              style={{
                padding: '10px 20px',
                background: bancosModificados && !salvandoConfiguracoes ? '#007bff' : '#ccc',
                border: 'none',
                borderRadius: '6px',
                cursor: bancosModificados && !salvandoConfiguracoes ? 'pointer' : 'not-allowed',
                fontSize: '14px',
                fontWeight: 500,
                color: 'white',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (bancosModificados && !salvandoConfiguracoes) {
                  e.currentTarget.style.background = '#0056b3';
                }
              }}
              onMouseLeave={(e) => {
                if (bancosModificados && !salvandoConfiguracoes) {
                  e.currentTarget.style.background = '#007bff';
                }
              }}
            >
              {salvandoConfiguracoes ? 'Salvando...' : 'Salvar'}
            </button>
            <button
              onClick={() => setShowConfiguracoes(false)}
              style={{
                padding: '10px 20px',
                background: '#f0f2f5',
                border: '1px solid #e0e0e0',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 500,
                color: '#333',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#e8eaed')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#f0f2f5')}
            >
              Fechar
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default VisualizacaoAnalista;
